import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Typography,
  DatePicker,
  Space,
} from "antd";
import {
  DollarOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Line, Pie, Column } from "@ant-design/plots";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);
import api from "../../../api/axios";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const CreditReport = () => {
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs(),
  ]);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    const res = await api.get("/api/credit");
    setSales(res.data || []);
  };

  // ================= FILTER =================
  useEffect(() => {
    const [start, end] = dateRange;

    const filtered = sales.filter((sale) =>
      dayjs(sale.createdAt).isBetween(start, end, null, "[]")
    );

    setFilteredSales(filtered);
  }, [dateRange, sales]);

  // ================= KPIs =================
  const kpi = useMemo(() => {
    const total = filteredSales.reduce((s, x) => s + x.grandTotal, 0);
    const collected = filteredSales
      .filter((x) => x.isCollected)
      .reduce((s, x) => s + x.grandTotal, 0);

    const pending = total - collected;

    const overdue = filteredSales.filter((sale) => {
      const days =
        (new Date() - new Date(sale.createdAt)) /
        (1000 * 60 * 60 * 24);
      return !sale.isCollected && days > 30;
    }).length;

    return {
      total,
      collected,
      pending,
      collectionRate: total ? (collected / total) * 100 : 0,
      overdueRate:
        filteredSales.length > 0
          ? (overdue / filteredSales.length) * 100
          : 0,
    };
  }, [filteredSales]);

  // ================= TREND DATA =================
  const trendData = useMemo(() => {
    const map = {};

    filteredSales.forEach((sale) => {
      const month = dayjs(sale.createdAt).format("MMM YYYY");

      if (!map[month]) map[month] = 0;
      map[month] += sale.grandTotal;
    });

    return Object.entries(map).map(([month, total]) => ({
      month,
      total,
    }));
  }, [filteredSales]);

  // ================= SHOP COMPARISON =================
  const shopData = useMemo(() => {
    const map = {};

    filteredSales.forEach((sale) => {
      const shop = sale.shop?.name || "Unknown";

      if (!map[shop]) map[shop] = 0;
      map[shop] += sale.grandTotal;
    });

    return Object.entries(map).map(([shop, total]) => ({
      shop,
      total,
    }));
  }, [filteredSales]);

  const columns = [
    { title: "Invoice", dataIndex: "id" },
    {
      title: "Customer",
      render: (_, r) => r.customer?.name,
    },
    {
      title: "Shop",
      render: (_, r) => r.shop?.name,
    },
    {
      title: "Amount",
      dataIndex: "grandTotal",
      render: (v) => `$${v.toFixed(2)}`,
    },
    {
      title: "Status",
      render: (_, r) =>
        r.isCollected ? (
          <Tag color="green">Collected</Tag>
        ) : (
          <Tag color="orange">Pending</Tag>
        ),
    },
  ];

  return (
    <div style={{ padding: 30, }}>
      <Title level={2}>Credit Intelligence Dashboard</Title>

      {/* DATE FILTER */}
      <Card style={{ marginBottom: 20 }}>
        <Space>
          <span>Select Period:</span>
          <RangePicker
            value={dateRange}
            onChange={(val) => setDateRange(val)}
          />
        </Space>
      </Card>

      {/* KPI SECTION */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Credit"
              value={kpi.total}
              precision={2}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Statistic
              title="Collection Rate"
              value={kpi.collectionRate}
              suffix="%"
              precision={1}
              valueStyle={{ color: "green" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Amount"
              value={kpi.pending}
              precision={2}
              valueStyle={{ color: "orange" }}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Statistic
              title="Overdue Rate"
              value={kpi.overdueRate}
              suffix="%"
              precision={1}
              valueStyle={{ color: "red" }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* CHARTS */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={12}>
          <Card title="Credit Trend">
            <Line
              data={trendData}
              xField="month"
              yField="total"
              smooth
              height={300}
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Credit by Shop">
            <Column
              data={shopData}
              xField="shop"
              yField="total"
              height={300}
            />
          </Card>
        </Col>
      </Row>

      {/* FULL TABLE */}
      <Card title="Detailed Credit Records">
        <Table
          columns={columns}
          dataSource={filteredSales}
          rowKey="id"
          pagination={{ pageSize: 8 }}
        />
      </Card>
    </div>
  );
};

export default CreditReport;
